// src/frontend/src/pages/ComparisonPage.tsx
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import HomeLayout from '../components/Layout/HomeLayout';

interface ComparisonFeature {
  feature: string;
  pmCoach: boolean | string;
  competitor: boolean | string;
  advantage?: string;
}

interface ComparisonContent {
  title: string;
  subtitle: string;
  competitorName: string;
  competitorDescription: string;
  keyDifferences: string[];
  comparisonTable: ComparisonFeature[];
  pmCoachAdvantages: {
    title: string;
    description: string;
    icon: string;
  }[];
  testimonial?: {
    quote: string;
    author: string;
    role: string;
  };
  ctaTitle: string;
  ctaDescription: string;
}

const comparisonData: Record<string, ComparisonContent> = {
  'betterup': {
    title: 'PM Coach vs BetterUp',
    subtitle: 'PM-specific coaching vs generic leadership development',
    competitorName: 'BetterUp',
    competitorDescription: 'BetterUp offers general leadership coaching and personal development for executives and managers across all functions.',
    keyDifferences: [
      'PM Coach focuses specifically on stakeholder conversations PMs face daily',
      'Practice real scenarios vs generic leadership advice',
      'AI-powered simulations vs human coach dependency',
      '$19-39/month vs $300-500/month pricing',
      'Immediate access vs waiting for coach availability'
    ],
    comparisonTable: [
      {
        feature: 'PM-Specific Content',
        pmCoach: true,
        competitor: false,
        advantage: 'Every scenario designed for PM challenges'
      },
      {
        feature: 'Stakeholder Simulations',
        pmCoach: true,
        competitor: false,
        advantage: 'Practice with realistic AI stakeholders'
      },
      {
        feature: 'Real-time Coaching',
        pmCoach: true,
        competitor: false,
        advantage: 'Get tips as you type responses'
      },
      {
        feature: 'Available 24/7',
        pmCoach: true,
        competitor: false,
        advantage: 'Practice anytime, no scheduling needed'
      },
      {
        feature: 'Scenario Library',
        pmCoach: '50+ PM scenarios',
        competitor: 'Generic case studies',
        advantage: 'PM-specific situations you face daily'
      },
      {
        feature: 'Pricing',
        pmCoach: '$19-39/month',
        competitor: '$300-500/month',
        advantage: '10x more affordable'
      },
      {
        feature: 'Time to Value',
        pmCoach: 'Hours',
        competitor: 'Months',
        advantage: 'See results immediately'
      },
      {
        feature: 'Human Coaching',
        pmCoach: false,
        competitor: true,
        advantage: 'AI available 24/7 vs limited coach availability'
      }
    ],
    pmCoachAdvantages: [
      {
        title: 'PM-Specific Focus',
        description: 'Every feature, scenario, and piece of feedback is designed specifically for project management challenges. No generic leadership advice.',
        icon: '🎯'
      },
      {
        title: 'Hands-On Practice',
        description: 'Practice actual conversations with AI stakeholders instead of just talking about leadership concepts.',
        icon: '💪'
      },
      {
        title: '10x More Affordable',
        description: 'Get better PM-specific results for less than the cost of a Netflix subscription vs $500+/month executive coaching.',
        icon: '💰'
      }
    ],
    testimonial: {
      quote: 'I tried BetterUp but it was too generic. PM Coach gives me exactly the practice I need for stakeholder conversations I face every day as a PM.',
      author: 'Sarah Chen',
      role: 'Senior PM at TechCorp'
    },
    ctaTitle: 'Experience PM-Specific Coaching',
    ctaDescription: 'See why thousands of PMs choose focused practice over generic advice.'
  },
  'traditional-pm-training': {
    title: 'PM Coach vs Traditional PM Training',
    subtitle: 'Practice-based learning vs theoretical frameworks',
    competitorName: 'Traditional PM Training',
    competitorDescription: 'Traditional PM training includes PMP certification courses, corporate training programs, and university courses focused on PM frameworks and processes.',
    keyDifferences: [
      'Practical conversation skills vs theoretical knowledge',
      'Interactive AI simulations vs static case studies',
      'Real-time feedback vs end-of-course assessments',
      'Ongoing skill development vs one-time certification',
      'Affordable monthly access vs expensive bootcamps'
    ],
    comparisonTable: [
      {
        feature: 'Conversation Practice',
        pmCoach: true,
        competitor: false,
        advantage: 'Practice actual stakeholder conversations'
      },
      {
        feature: 'Interactive Learning',
        pmCoach: 'AI simulations',
        competitor: 'Reading & tests',
        advantage: 'Dynamic practice vs passive learning'
      },
      {
        feature: 'Real-time Feedback',
        pmCoach: true,
        competitor: false,
        advantage: 'Immediate improvement suggestions'
      },
      {
        feature: 'Ongoing Development',
        pmCoach: true,
        competitor: false,
        advantage: 'Continuous skill building'
      },
      {
        feature: 'Practical Application',
        pmCoach: 'Immediate',
        competitor: 'Theoretical',
        advantage: 'Apply skills in real scenarios'
      },
      {
        feature: 'Cost Effectiveness',
        pmCoach: '$19-39/month',
        competitor: '$3,000-8,000',
        advantage: 'Affordable ongoing development'
      },
      {
        feature: 'Certification',
        pmCoach: false,
        competitor: true,
        advantage: 'Focus on skills vs credentials'
      },
      {
        feature: 'Framework Knowledge',
        pmCoach: 'Applied',
        competitor: 'Theoretical',
        advantage: 'Learn frameworks through practice'
      }
    ],
    pmCoachAdvantages: [
      {
        title: 'Practice Over Theory',
        description: 'Master the conversations that actually determine PM success rather than memorizing frameworks you may never use.',
        icon: '🎪'
      },
      {
        title: 'Immediate Application',
        description: 'Use what you learn in your next stakeholder meeting, not months later after forgetting course content.',
        icon: '⚡'
      },
      {
        title: 'Continuous Growth',
        description: 'Ongoing skill development vs one-time training that becomes outdated quickly.',
        icon: '📈'
      }
    ],
    ctaTitle: 'Choose Practice Over Theory',
    ctaDescription: 'Build real PM skills that make an immediate impact on your career.'
  },
    'coachhub': {
    title: 'PM Coach vs CoachHub',
    subtitle: 'PM-specific coaching vs general professional development',
    competitorName: 'CoachHub',
    competitorDescription: 'CoachHub offers general professional development coaching and leadership training for employees across all functions and industries.',
    keyDifferences: [
      'PM Coach focuses exclusively on project management stakeholder conversations',
      'Practice actual PM scenarios vs generic leadership development',
      'AI-powered 24/7 availability vs human coach scheduling',
      '$19-39/month vs $200-400/month pricing',
      'Immediate skill application vs long-term development focus'
    ],
    comparisonTable: [
      {
        feature: 'PM-Specific Content',
        pmCoach: true,
        competitor: false,
        advantage: 'Every scenario designed for PM stakeholder challenges'
      },
      {
        feature: 'Stakeholder Conversation Practice',
        pmCoach: true,
        competitor: false,
        advantage: 'Practice realistic PM scenarios with AI stakeholders'
      },
      {
        feature: 'Industry Focus',
        pmCoach: 'Project Management',
        competitor: 'General Leadership',
        advantage: 'Laser-focused on PM skills vs broad development'
      },
      {
        feature: 'Availability',
        pmCoach: '24/7 AI coaching',
        competitor: 'Scheduled human sessions',
        advantage: 'Practice anytime without booking constraints'
      },
      {
        feature: 'Pricing',
        pmCoach: '$19-39/month',
        competitor: '$200-400/month',
        advantage: '5-10x more affordable'
      },
      {
        feature: 'Time to Value',
        pmCoach: 'Immediate',
        competitor: '3-6 months',
        advantage: 'Start improving stakeholder conversations today'
      },
      {
        feature: 'Content Volume',
        pmCoach: '50+ PM scenarios',
        competitor: 'General coaching topics',
        advantage: 'Deep library of PM-specific practice opportunities'
      },
      {
        feature: 'Human Coaching',
        pmCoach: false,
        competitor: true,
        advantage: 'AI available 24/7 vs limited human availability'
      },
      {
        feature: 'Corporate Programs',
        pmCoach: 'PM team focused',
        competitor: 'Company-wide',
        advantage: 'Designed specifically for PMO and PM team needs'
      }
    ],
    pmCoachAdvantages: [
      {
        title: 'PM Expertise',
        description: 'Every feature is designed by PMs for PMs. No generic leadership advice - just practical stakeholder conversation skills you need daily.',
        icon: '🎯'
      },
      {
        title: 'Immediate Practice',
        description: 'Start practicing difficult conversations today. No waiting for coach availability or scheduling around busy calendars.',
        icon: '⚡'
      },
      {
        title: 'Cost Effectiveness',
        description: 'Get better PM-specific results for less than the cost of a gym membership vs expensive executive coaching rates.',
        icon: '💰'
      }
    ],
    testimonial: {
      quote: 'CoachHub helped with general leadership, but PM Coach gives me exactly what I need - practice handling scope creep, executive pushback, and team conflicts. Much more practical for my daily PM work.',
      author: 'Jennifer Liu',
      role: 'Senior PM at FinTech Startup'
    },
    ctaTitle: 'Choose PM-Specific Over Generic',
    ctaDescription: 'Get targeted PM skills development that makes an immediate impact on your projects.'
  },

  'executive-coaching': {
    title: 'PM Coach vs Executive Coaching',
    subtitle: 'Affordable PM development vs expensive 1-on-1 coaching',
    competitorName: 'Executive Coaching',
    competitorDescription: 'Traditional executive coaching provides 1-on-1 leadership development with experienced coaches, typically reserved for senior executives and costing $300-800 per hour.',
    keyDifferences: [
      'PM Coach costs $19-39/month vs $500-800/hour for executive coaching',
      'Available to all PMs vs restricted to senior executives',
      'PM-specific scenarios vs general leadership development',
      'Practice-based learning vs advice-only sessions',
      '24/7 availability vs scheduled appointments'
    ],
    comparisonTable: [
      {
        feature: 'Cost per Month',
        pmCoach: '$19-39',
        competitor: '$2,000-3,200',
        advantage: '50-100x more affordable'
      },
      {
        feature: 'Accessibility',
        pmCoach: 'All PM levels',
        competitor: 'Senior executives only',
        advantage: 'Available to junior, mid, and senior PMs'
      },
      {
        feature: 'PM-Specific Content',
        pmCoach: true,
        competitor: false,
        advantage: 'Focused on stakeholder conversation skills PMs need'
      },
      {
        feature: 'Practice Opportunities',
        pmCoach: 'Unlimited scenarios',
        competitor: 'Discussion only',
        advantage: 'Hands-on practice vs theoretical advice'
      },
      {
        feature: 'Availability',
        pmCoach: '24/7',
        competitor: '1-2 hours/month',
        advantage: 'Practice whenever you need it'
      },
      {
        feature: 'Scalability',
        pmCoach: 'Entire PM teams',
        competitor: 'Individual only',
        advantage: 'Scale PM development across organizations'
      },
      {
        feature: 'Personalized Attention',
        pmCoach: false,
        competitor: true,
        advantage: 'AI adapts to your responses and provides personalized feedback'
      },
      {
        feature: 'Industry Networks',
        pmCoach: false,
        competitor: true,
        advantage: 'Focus on skill development over networking'
      },
      {
        feature: 'Immediate Application',
        pmCoach: true,
        competitor: 'Limited',
        advantage: 'Practice scenarios you face in your next meeting'
      }
    ],
    pmCoachAdvantages: [
      {
        title: 'Democratic Access',
        description: 'Executive coaching is reserved for senior leaders. PM Coach gives every PM access to world-class stakeholder management development.',
        icon: '🌍'
      },
      {
        title: 'Practice-Based Learning',
        description: 'Instead of just talking about difficult conversations, you actually practice them in realistic scenarios with immediate feedback.',
        icon: '🎭'
      },
      {
        title: 'Exponential Value',
        description: 'Get 50-100x more coaching value per dollar spent, with unlimited practice opportunities vs limited hourly sessions.',
        icon: '📈'
      }
    ],
    testimonial: {
      quote: 'I was on the waiting list for executive coaching for 2 years. PM Coach gave me the stakeholder management skills I needed immediately, and I got promoted before the coaching slot even opened up.',
      author: 'Marcus Williams',
      role: 'Director of PMO at Healthcare Corp'
    },
    ctaTitle: 'Get Executive-Level Development at Any Level',
    ctaDescription: 'Why wait for executive coaching when you can develop world-class PM skills starting today?'
  }

  // Add more comparison data as needed
};

const ComparisonPage: React.FC = () => {
  const { competitor } = useParams<{ competitor: string }>();
  
  if (!competitor || !comparisonData[competitor]) {
    return (
      <HomeLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Comparison Not Found</h1>
          <p className="text-gray-600 mb-8">The comparison you're looking for doesn't exist.</p>
          <Link to="/" className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
            Back to Home
          </Link>
        </div>
      </HomeLayout>
    );
  }

  const comparison = comparisonData[competitor];

  return (
    <HomeLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="py-16 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6">
            {comparison.title}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            {comparison.subtitle}
          </p>
          <p className="text-lg text-gray-700 max-w-4xl mx-auto mb-8">
            {comparison.competitorDescription}
          </p>
        </div>

        {/* Key Differences */}
        <div className="py-16 bg-blue-50 rounded-2xl mb-16">
          <div className="max-w-4xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
              Key Differences
            </h2>
            <div className="space-y-4">
              {comparison.keyDifferences.map((difference, index) => (
                <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm">
                  <svg className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-gray-700 text-lg">{difference}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="py-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Detailed Comparison
          </h2>
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center font-semibold">PM Coach</th>
                    <th className="px-6 py-4 text-center font-semibold">{comparison.competitorName}</th>
                    <th className="px-6 py-4 text-left font-semibold">PM Coach Advantage</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {comparison.comparisonTable.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">{row.feature}</td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.pmCoach === 'boolean' ? (
                          row.pmCoach ? (
                            <svg className="w-6 h-6 text-green-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-red-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          )
                        ) : (
                          <span className="text-blue-600 font-medium">{row.pmCoach}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {typeof row.competitor === 'boolean' ? (
                          row.competitor ? (
                            <svg className="w-6 h-6 text-green-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-6 h-6 text-red-500 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          )
                        ) : (
                          <span className="text-gray-600">{row.competitor}</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-700 text-sm">{row.advantage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* PM Coach Advantages */}
        <div className="py-16 bg-gray-50 rounded-2xl">
          <div className="max-w-6xl mx-auto px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
              Why PM Coach Wins
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {comparison.pmCoachAdvantages.map((advantage, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl mb-4">{advantage.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{advantage.title}</h3>
                  <p className="text-gray-600">{advantage.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Testimonial */}
        {comparison.testimonial && (
          <div className="py-16">
            <div className="max-w-4xl mx-auto text-center">
              <blockquote className="text-2xl font-medium text-gray-900 mb-6">
                "{comparison.testimonial.quote}"
              </blockquote>
              <div className="text-gray-600">
                <div className="font-semibold">{comparison.testimonial.author}</div>
                <div>{comparison.testimonial.role}</div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl text-white text-center">
          <h2 className="text-3xl font-bold mb-4">{comparison.ctaTitle}</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {comparison.ctaDescription}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-gray-100"
            >
              Try PM Coach Free
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center px-8 py-3 border-2 border-white text-white rounded-lg hover:bg-white hover:text-blue-600"
            >
              See Pricing
            </Link>
          </div>
          <p className="text-blue-200 text-sm mt-4">
            14-day free trial • No credit card required • Start in minutes
          </p>
        </div>
      </div>
    </HomeLayout>
  );
};

export default ComparisonPage;